import api from './api';

export interface Connection {
  radacctid: number;
  acctsessionid: string;
  acctuniqueid: string;
  username: string;
  realm?: string;
  nasipaddress: string;
  nasportid?: string;
  nasporttype?: string;
  acctstarttime?: string;
  acctupdatetime?: string;
  acctstoptime?: string;
  acctinterval?: number;
  acctsessiontime?: number;
  acctauthentic?: string;
  connectinfo_start?: string;
  connectinfo_stop?: string;
  acctinputoctets?: number;
  acctoutputoctets?: number;
  calledstationid: string;
  callingstationid: string;
  acctterminatecause: string;
  servicetype?: string;
  framedprotocol?: string;
  framedipaddress: string;
  framedipv6address: string;
  framedipv6prefix: string;
  framedinterfaceid: string;
  delegatedipv6prefix: string;
  class?: string;
}

export interface ConnectionStats {
  totalSessions: number;
  totalTime: number;
  totalUpload: number;
  totalDownload: number;
  avgSessionTime: number;
}

export interface ConnectionResponse {
  success: boolean;
  data: Connection[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: ConnectionStats;
  contractInfo: {
    id: number;
    title: string;
    username: string;
    status: string;
  };
}

export interface ActiveConnectionsResponse {
  success: boolean;
  data: Connection[];
  contractInfo: {
    id: number;
    title: string;
    username: string;
    status: string;
  };
}

export interface ConnectionStatsResponse {
  success: boolean;
  data: ConnectionStats;
  contractInfo: {
    id: number;
    title: string;
    username: string;
    status: string;
  };
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
}

export interface ConnectionDetailsResponse {
  success: boolean;
  data: Connection;
}

export interface GetConnectionsParams {
  page?: number;
  limit?: number;
  onlyActive?: boolean;
  startDate?: string;
  endDate?: string;
  // Filtro opcional por serviço do contrato
  serviceId?: number;
}

export interface GetAllConnectionsParams {
  page?: number;
  limit?: number;
  onlyActive?: boolean;
  onlyClosed?: boolean;
  startDate?: string;
  endDate?: string;
  username?: string;
  ip?: string;
}

export interface GetAllConnectionsResponse {
  success: boolean;
  data: Connection[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface RadPostAuthRecord {
  id: number;
  username: string;
  pass: string;
  reply: string;
  authdate: string;
}

export interface GetTentativasParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  username?: string;
  reply?: string;
}

export interface GetTentativasResponse {
  success: boolean;
  data: RadPostAuthRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export class ConnectionService {
  // Buscar todas as conexões (relatório geral)
  static async getAll(params?: GetAllConnectionsParams): Promise<GetAllConnectionsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.onlyActive) queryParams.append('onlyActive', params.onlyActive.toString());
    if (params?.onlyClosed) queryParams.append('onlyClosed', params.onlyClosed.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.username) queryParams.append('username', params.username);
    if (params?.ip) queryParams.append('ip', params.ip);

    const queryString = queryParams.toString();
    const url = `/connections${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response as unknown as GetAllConnectionsResponse;
  }

  // Buscar tentativas de autenticação (radpostauth)
  static async getTentativas(params?: GetTentativasParams): Promise<GetTentativasResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.username) queryParams.append('username', params.username);
    if (params?.reply) queryParams.append('reply', params.reply);

    const queryString = queryParams.toString();
    const url = `/connections/tentativas${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response as unknown as GetTentativasResponse;
  }

  // Limpar todos os registros da tabela de tentativas (radpostauth)
  static async clearTentativas(): Promise<{ success: boolean; message: string; deletedCount: number }> {
    const response = await api.delete('/connections/tentativas');
    return response as unknown as { success: boolean; message: string; deletedCount: number };
  }

  // Derrubar conexão de um usuário
  static async disconnectUser(username: string): Promise<{ success: boolean; message: string; disconnectedSessions: number }> {
    const response = await api.post('/connections/disconnect', { username });
    return response as unknown as { success: boolean; message: string; disconnectedSessions: number };
  }

  // Contar sessões ativas presas (sem atualização há mais de 1 hora)
  static async countSessoesPresas(): Promise<{ success: boolean; data: { totalSessoesPresas: number } }> {
    const response = await api.get('/connections/sessoes-presas/count');
    return response as unknown as { success: boolean; data: { totalSessoesPresas: number } };
  }

  // Fechar sessões ativas presas
  static async repararSessoesPresas(): Promise<{ success: boolean; message: string; data: { closedSessions: number } }> {
    const response = await api.post('/connections/sessoes-presas/reparar');
    return response as unknown as { success: boolean; message: string; data: { closedSessions: number } };
  }

  // Buscar conexões por contrato
  static async getByContract(
    contractId: number,
    params?: GetConnectionsParams
  ): Promise<ConnectionResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.onlyActive) queryParams.append('onlyActive', params.onlyActive.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.serviceId) queryParams.append('serviceId', params.serviceId.toString());

    const queryString = queryParams.toString();
    const url = `/connections/contract/${contractId}${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);

    return response as unknown as ConnectionResponse; // Cast duplo para resolver conflito de tipos
  }

  // Buscar conexões ativas por contrato
  static async getActiveByContract(contractId: number): Promise<ActiveConnectionsResponse> {
    const response = await api.get(`/connections/contract/${contractId}/active`);
    return response as unknown as ActiveConnectionsResponse; // Cast duplo para resolver conflito de tipos
  }

  // Buscar estatísticas de conexão por contrato
  static async getStatsByContract(
    contractId: number,
    days: number = 30
  ): Promise<ConnectionStatsResponse> {
    const response = await api.get(`/connections/contract/${contractId}/stats?days=${days}`);
    return response as unknown as ConnectionStatsResponse; // Cast duplo para resolver conflito de tipos
  }

  // Buscar detalhes de uma conexão específica
  static async getConnectionDetails(connectionId: number): Promise<ConnectionDetailsResponse> {
    const response = await api.get(`/connections/${connectionId}`);
    return response as unknown as ConnectionDetailsResponse; // Cast duplo para resolver conflito de tipos
  }

  // Utilitários para formatação
  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  static getStatusLabel(connection: Connection): string {
    if (connection.acctstoptime) {
      return 'Desconectado';
    } else if (connection.acctstarttime) {
      return 'Conectado';
    } else {
      return 'Desconhecido';
    }
  }

  static getStatusColor(connection: Connection): 'success' | 'error' | 'default' {
    if (connection.acctstoptime) {
      return 'error';
    } else if (connection.acctstarttime) {
      return 'success';
    } else {
      return 'default';
    }
  }

  static getTerminateCauseLabel(cause?: string): string {
    if (!cause) return 'N/A';

    const causes: { [key: string]: string } = {
      'User-Request': 'Solicitação do Usuário',
      'Lost-Carrier': 'Perda de Sinal',
      'Idle-Timeout': 'Timeout por Inatividade',
      'Session-Timeout': 'Timeout de Sessão',
      'Admin-Reset': 'Reset Administrativo',
      'Admin-Reboot': 'Reboot Administrativo',
      'Port-Error': 'Erro na Porta',
      'NAS-Error': 'Erro no NAS',
      'NAS-Request': 'Solicitação do NAS',
      'NAS-Reboot': 'Reboot do NAS',
      'Port-Unneeded': 'Porta Desnecessária',
      'Port-Preempted': 'Porta Preemptada',
      'Port-Suspended': 'Porta Suspensa',
      'Service-Unavailable': 'Serviço Indisponível',
      'Callback': 'Callback',
      'User-Error': 'Erro do Usuário',
      'Host-Request': 'Solicitação do Host',
    };

    return causes[cause] || cause;
  }
}

export default ConnectionService; 